import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Prompt } from 'react-router-dom';
import {
  Box,
  costFormater,
  Enrollment,
  Inscription,
  NavigationRightIcon,
  Preloader,
  PriceInput,
  Text,
  useResponsive,
} from '@common-fe/common-fe';
import _ from 'lodash';
import styled from 'styled-components';

import AppButton from '@/components/controls/AppButton';
import ServerErrorModal from '@/modules/investments/modal/ServerErrorModal';
import theme from '@/styles/theme';

import AutoInvestmentReminder, {
  useAutoInvestmentReminder,
} from '../../AutoInvestment/AutoInvestmentReminder';
import { useInvestmentsValidation } from '../../hooks';
import { InvestmentCardBlock } from '../../InvestmentCardBlock';
import { useDefaultAllocationAmount } from '../../NewInvestments/hook';
import { useTradeInvestmentsQuery } from '../../queries';
import DisclosureFooter from '../DisclosureFooter';
import { useFundingAmountStore } from '../stores/useFundingAmount.store';

import ConfirmSellModal from './ConfirmSellModal';

export const PriceControl = styled(PriceInput)`
  ${() => theme.controls.mediumSize};
  min-height: 40px;
`;
interface Props {
  accountId: string;
  onCancel: () => void;
  onNext: () => void;
  currentMyAccount: Enrollment;
  isSwitchMode?: boolean;
  stepLength: number;
  step: number;
  successVisible?: boolean;
}

const FundingAmount: React.FC<Props> = ({
  onCancel,
  onNext,
  currentMyAccount,
  stepLength,
  step,
  accountId,
  isSwitchMode,
  successVisible,
}) => {
  const { isMobile } = useResponsive();
  const { t } = useTranslation();
  const { values, handleSetValues } = useFundingAmountStore();
  const { isDisplayedReminder } = useAutoInvestmentReminder();
  const defaultInitialAmount = useDefaultAllocationAmount();
  const [investingAmount, setInvestingAmount] = useState(defaultInitialAmount || '');
  const [showErrors, setShowErrors] = useState(false);
  const [sellModalVisible, setSellModalVisible] = useState(false);
  const [showServerErrorModal, setShowServerErrorModal] = useState(false);
  const [isFundsLoading, setFundsLoading] = useState(false);
  const [succesButton, setSuccesButton] = useState(false);

  const formatedMyAccount = useMemo(
    () => ({
      ...currentMyAccount,
      available: currentMyAccount?.available,
    }),
    [currentMyAccount]
  );
  const availableBalance = useMemo(() => {
    return currentMyAccount?.available - currentMyAccount?.investmentThreshold > 0
      ? currentMyAccount?.available - currentMyAccount?.investmentThreshold
      : 0;
  }, [currentMyAccount?.available, currentMyAccount?.investmentThreshold]);

  const { isLoading, handleSave } = useTradeInvestmentsQuery({ isSellMode: true });

  const handleSellInvestments = useCallback(async () => {
    try {
      await handleSave({
        amount: _.toNumber(investingAmount),
      });
      onNext();
      handleSetValues({
        fundingAmount: investingAmount,
      });
    } catch {
      setShowServerErrorModal(true);
    }
  }, [handleSave, handleSetValues, investingAmount, onNext]);

  const { error } = useInvestmentsValidation({
    investingAmount: investingAmount,
    availableToInvest: availableBalance,
    isSellingMode: false,
    minimumInvestmentAmount: currentMyAccount?.minimumInvestmentAmount,
  });
  const handleSubmit = useCallback(async () => {
    setShowErrors(true);
    if (error) {
      return null;
    }
    setFundsLoading(true);

    if (succesButton) {
      return onNext();
    }
    if (!error) {
      setTimeout(() => {
        setSuccesButton(true);
        setTimeout(() => {
          setSuccesButton(false);
          setFundsLoading(false);
          onNext();

          handleSetValues({
            fundingAmount: investingAmount,
          });
        }, 1500);
      }, 2000);
    }
    return {};
  }, [error, succesButton, onNext, handleSetValues, investingAmount]);

  useEffect(() => {
    if (values?.fundingAmount) {
      setInvestingAmount(values?.fundingAmount);
    }
  }, [values]);
  const handleMaximumAllowed = useCallback(() => {
    setInvestingAmount(String(availableBalance));
  }, [availableBalance]);
  return (
    <Box direction="column" data-testid="Funding-wrapper-id">
      <ConfirmSellModal
        fundingAmount={investingAmount}
        isLoading={isLoading}
        visible={sellModalVisible}
        onSetVisible={setSellModalVisible}
        onSubmit={handleSellInvestments}
      />

      <ServerErrorModal
        visible={showServerErrorModal}
        isLoading={isLoading}
        onSetVisible={setShowServerErrorModal}
        onSendRequest={handleSellInvestments}
        style={{ zIndex: 10000, position: 'fixed' }}
      />
      <Prompt
        when={!!investingAmount && !successVisible && !isSwitchMode}
        message="You have unsaved changes, are you sure you want to leave?"
      />
      <Box flex="grow">
        <Box
          direction="column"
          pad={
            isMobile
              ? {
                  horizontal: 'spacing16',
                }
              : undefined
          }
        >
          <Text
            size={isMobile ? '2xl' : '3xl'}
            color="blockTitle"
            data-testid="invest-more_title"
            margin={{ bottom: 'spacing12' }}
            weight={700}
          >
            {t('Invest more Funds')}
          </Text>
          <Text size="large" margin={{ bottom: 'spacing24' }}>
            {t('Move money from your cash account to your investment account in a few easy steps.')}
          </Text>
        </Box>
        <Box
          direction={isMobile ? 'column' : 'row'}
          background="module"
          align="start"
          round="moduleRound"
          pad={isMobile ? 'spacing16' : 'spacing24'}
          margin={isMobile ? { bottom: 'spacing16' } : { bottom: 'spacing24' }}
        >
          {formatedMyAccount && (
            <InvestmentCardBlock
              enrollment={formatedMyAccount}
              style={isMobile ? { minWidth: '100%', maxWidth: '100%' } : undefined}
            />
          )}
          <Box
            direction="row"
            height={isMobile ? undefined : '248px'}
            align="center"
            justify="center"
            width={isMobile ? '100%' : undefined}
            margin={isMobile ? { vertical: 'spacing8' } : { horizontal: 'spacing12' }}
          >
            <NavigationRightIcon style={isMobile ? { transform: 'rotate(90deg)' } : undefined} />
          </Box>
          <Box direction="column">
            <Box
              direction="column"
              pad="spacing24"
              height="248px"
              background="canvas"
              round="container1Round"
              data-testid="invest_more_funds-wrapper"
              border={{ size: 'small', color: 'border2' }}
            >
              <Text margin={{ bottom: 'spacing12' }} data-testid="invest-more_card_title" size="xxlarge">
                {t('Invest more Funds')}
              </Text>
              <Text margin={{ bottom: 'spacing24' }} size="medium">
                {t(
                  'We will transfer this amount from your cash account to your investment account as soon as the trade is executed.'
                )}
              </Text>
              <Box direction="row" align="center">
                <Box width="280px" margin={{ right: 'spacing24' }} data-testid="Price-control-wrapper-id">
                  <PriceControl
                    isClearMode
                    name="Funding amount"
                    placeholder={t('Enter $ amount')}
                    onChange={setInvestingAmount}
                    onIconClick={() => setInvestingAmount('')}
                    value={investingAmount}
                    disabled={isLoading || succesButton}
                    autoFocus
                  />
                </Box>
                <Inscription onClick={handleMaximumAllowed} cursor="pointer" size="medium" color="textAccent">
                  {`${t('Max.')} ${costFormater(availableBalance || 0, {
                    isPrecision: true,
                  })}`}
                </Inscription>
              </Box>
            </Box>
            {isDisplayedReminder && <AutoInvestmentReminder accountId={accountId} />}
          </Box>
        </Box>
        <Box direction="column" margin={isMobile ? { horizontal: 'spacing16' } : undefined}>
          {error && showErrors && (
            <Box
              align="end"
              width="100%"
              margin={isMobile ? { bottom: 'spacing8' } : { bottom: 'spacing24' }}
            >
              <Text size="medium" color="danger" style={{ lineHeight: '20px' }}>
                {t(error)}
              </Text>
            </Box>
          )}
          <Box direction="row" align="center" justify="end">
            <Box gap="spacing12" fill={isMobile} direction="row" justify="end" align="center">
              <Box fill={isMobile}>
                <AppButton
                  fill={isMobile}
                  onClick={() => onCancel()}
                  buttonType="secondary"
                  testId="funding-amount_back"
                  width="130px"
                >
                  {t('Back')}
                </AppButton>
              </Box>

              <Box fill={isMobile}>
                <AppButton
                  type="submit"
                  fill={isMobile}
                  onClick={() => handleSubmit()}
                  successMode={succesButton}
                  testId="funding-amount_next"
                  width="control"
                >
                  {isLoading || isFundsLoading ? (
                    <Preloader color="white" />
                  ) : (
                    `${t('Next')}  (${step}/${stepLength})`
                  )}
                </AppButton>
              </Box>
            </Box>
          </Box>
          <DisclosureFooter />
        </Box>
      </Box>
    </Box>
  );
};

export default FundingAmount;
